/**
 * TypeScript interfaces and types for Bill Part Pricing Control composite
 */

import type { ComputedRef, Ref } from 'vue'
import type { PricingAction } from './constants'

// Branded types for better type safety
export type PartId = string & { readonly __brand: 'PartId' }
export type TransactionId = string & { readonly __brand: 'TransactionId' }
export type SellingOptionId = string & { readonly __brand: 'SellingOptionId' }
export type TransactionItemId = string & { readonly __brand: 'TransactionItemId' }
export type PartSku = string & { readonly __brand: 'PartSku' }
export type PartOem = string & { readonly __brand: 'PartOem' }
export type CurrencyCode = string & { readonly __brand: 'CurrencyCode' }
export type BuyingOptionId = string & { readonly __brand: 'BuyingOptionId' }

export interface PartPricingItem {
  id: string
  transactionItemId: TransactionItemId
  partId: PartId | null
  name: string
  sku: PartSku
  oem: PartOem
  currentPrice: number
  proposedPrice: number
  priceDifference: number
  priceDifferencePercent: number
  currency: CurrencyCode
  sellingOptionId: SellingOptionId | null
  supplierName: string
  lastUpdated: string
  action: PricingAction
  isNewPart?: boolean
  hasPriceDifference?: boolean
  availableBuyingOptions?: PartBuyingOption[]
  canCreateSellingOptions?: boolean
}

export interface PricingControlState {
  items: PartPricingItem[]
  isLoading: boolean
  isProcessing: boolean
  error: string | null
  hasUnsavedChanges: boolean
}

export interface PricingAnalysisResponse {
  items: PartPricingAnalysisItem[]
  summary: PricingAnalysisSummary
}

export interface PartPricingAnalysisItem {
  id: string
  transaction_item_id: TransactionItemId
  part_id: PartId | null
  name: string
  sku: PartSku
  oem: PartOem
  current_price: number
  proposed_price: number
  price_difference: number
  price_difference_percent: number
  currency: CurrencyCode
  selling_option_id: SellingOptionId | null
  supplier_name: string
  last_updated: string
  is_new_part?: boolean
  has_price_difference?: boolean
  available_buying_options?: {
    id: string
    name: string
    price: number
    currency: string
    supplier_name: string
    is_default: boolean
  }[]
  can_create_selling_options?: boolean
}

export interface PricingAnalysisSummary {
  total_items: number
  items_with_differences: number
  total_price_impact: number
}

export interface PartBuyingOption {
  id: BuyingOptionId
  name: string
  price: number
  currency: CurrencyCode
  supplierName: string
  isDefault: boolean
}

export interface CreateSellingOptionRequest {
  part_id: PartId
  buying_option_id?: BuyingOptionId
  option_type: 'linked' | 'independent'
  name: string
  price: number
  part_number?: string
  unit_type?: Record<string, unknown>
  unit_qt?: number
  supplier_id?: string
}

export interface CreateNewPartRequest {
  name: string
  sku?: string
  oem?: string
  description?: string
  is_leasable?: boolean
  rental_length?: number
  buying_price?: number
  selling_price: number
  supplier_id?: string
}

interface PricingDecisionBase<Action extends PricingAction> {
  action: Action
  transaction_item_id: TransactionItemId
}

export interface UpdatePricingDecision extends PricingDecisionBase<PricingAction.UPDATE> {
  part_id: PartId
  selling_option_id: SellingOptionId
  new_price: number
}

export interface CreateNewPartPricingDecision extends PricingDecisionBase<PricingAction.CREATE_NEW_PART> {
  part_id: null
  new_price: number
}

export interface CreateLinkedSellingOptionPricingDecision extends PricingDecisionBase<PricingAction.CREATE_SELLING_OPTION_LINKED> {
  part_id: PartId
  buying_option_id: BuyingOptionId
  option_type: 'linked'
  new_price: number
}

export interface CreateIndependentSellingOptionPricingDecision extends PricingDecisionBase<PricingAction.CREATE_SELLING_OPTION_INDEPENDENT> {
  part_id: PartId
  option_type: 'independent'
  new_price: number
}

export interface ExistingPartKeepOrSkipPricingDecision extends PricingDecisionBase<PricingAction.KEEP | PricingAction.SKIP> {
  part_id: PartId
  selling_option_id?: SellingOptionId
}

export interface NewPartKeepOrSkipPricingDecision extends PricingDecisionBase<PricingAction.KEEP | PricingAction.SKIP> {
  part_id: null
}

export type PricingDecision =
  | UpdatePricingDecision
  | CreateNewPartPricingDecision
  | CreateLinkedSellingOptionPricingDecision
  | CreateIndependentSellingOptionPricingDecision
  | ExistingPartKeepOrSkipPricingDecision
  | NewPartKeepOrSkipPricingDecision

export interface PricingDecisionsRequest {
  decisions: PricingDecision[]
}

export interface PricingDecisionsResponse {
  success?: boolean
  updated_count?: number
  errors?: string[]
  // Backend also returns this format for finalized bills:
  message?: string
  data?: boolean
  finalization?: {
    message: string
    success: boolean
  }
}

export interface BillPartPricingControlContext {
  // State
  state: Ref<PricingControlState>

  // Actions
  setItemAction: (itemId: string, action: PricingAction) => void
  applyPricingDecisions: () => Promise<void>
  resetChanges: () => void
  loadPricingAnalysis: (transactionId: string) => Promise<void>

  // Validation
  validatePricingDecisions: () => string[]
  validationErrors: ComputedRef<string[]>
  validationWarnings: ComputedRef<string[]>
  hasItemErrors: (itemId: string) => boolean

  // Computed
  pendingItems: ComputedRef<PartPricingItem[]>
  itemsToUpdate: ComputedRef<PartPricingItem[]>
  itemsToKeep: ComputedRef<PartPricingItem[]>
  canProceed: ComputedRef<boolean>
  hasChanges: ComputedRef<boolean>
  totalPriceImpact: ComputedRef<number>
  itemsWithSignificantChanges: ComputedRef<PartPricingItem[]>
}

export interface PricingValidationError {
  itemId: string
  field: string
  message: string
}

export interface PricingControlProps {
  transactionId: TransactionId
  readonly?: boolean
  showSummary?: boolean
}

export interface PricingControlEmits {
  (event: 'ready'): void
  (event: 'complete', decisions: PricingDecision[]): void
  (event: 'cancel'): void
  (event: 'error', error: string): void
  (event: 'change', hasChanges: boolean): void
}

export interface ApiError {
  status: number
  message: string
  data?: unknown
}

export interface PricingComparisonData {
  currentPrice: number
  proposedPrice: number
  difference: number
  percentageChange: number
  currency: string
  isIncrease: boolean
  isSignificant: boolean
}

export interface PricingControlOptions {
  showSupplierInfo?: boolean
  showLastUpdated?: boolean
  highlightThreshold?: number
  warningThreshold?: number
}

// Type assertion functions for branded types
export const asPartId = (id: string): PartId => id as PartId
export const asTransactionId = (id: string): TransactionId => id as TransactionId
export const asSellingOptionId = (id: string): SellingOptionId => id as SellingOptionId
export const asBuyingOptionId = (id: string): BuyingOptionId => id as BuyingOptionId
export const asTransactionItemId = (id: string): TransactionItemId => id as TransactionItemId
export const asPartSku = (sku: string): PartSku => sku as PartSku
export const asPartOem = (oem: string): PartOem => oem as PartOem
export const asCurrencyCode = (currency: string): CurrencyCode => currency as CurrencyCode
